import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiFileText,
  FiCalendar,
  FiUser,
  FiAward,
} from "react-icons/fi";
import { axiosget } from "../../helpers/httprequest";
import { Badge } from "../../components/ui/Badge";
import PageHeader from "../../components/PageHeader";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import EmptyState from "../../components/ui/EmptyState";
import moment from "../../helpers/moment";
import PayAcceptance from "./PayAcceptance";
import is_null from "../../helpers/is_null";
import VerifyPayment from "./VerifyPayment";
import PrintAcceptance from "./PrintAcceptance";
import PayNowButton from "./PayNowButton";
import AcceptanceWaiver from "./AcceptanceWaiver";

const Acceptance = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["acceptanceInformation"],
    queryFn: async () => {
      const req = await axiosget(`ms_applications/offered/admission`);
      return req?.data;
    },
  });

  const _remita_paid = (paid) => {
    if (is_null(paid)) {
      return { text: "Not generated", status: "pending" };
    }
    if (paid?.status === false) {
      return { text: `Ref: ${paid?.RRR || paid?.gatewayReference} - Not Paid`, status: "unpaid" };
    } else {
      return { text: `Ref: ${paid?.RRR || paid?.gatewayReference} - Paid`, status: "paid" };
    }
  };

  const _status = (data) => {
    switch (data) {
      case 0:
        return { text: "Pending", variant: "warning" };
      case 1:
        return { text: "Provisional Admission", variant: "success" };
      case 2:
        return { text: "Conditional Admission", variant: "info" };
      default:
        return { text: "Invalid option", variant: "neutral" };
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <>
      <PageHeader
        title="Acceptance"
        subtitle="Pay acceptance fee, print acceptance letter and receipt"
        icon={<FiAward className="w-5 h-5" />}
      />

      {isLoading && <Loader />}
      {error && <Alert variant="danger"><AlertDescription>{`Local server error: ${error}`}</AlertDescription></Alert>}
      {data?.type !== undefined && <Alert variant="danger"><AlertDescription>{data?.message}</AlertDescription></Alert>}

      {!isLoading && !error && data?.type === undefined && (!data || data.length === 0) && (
        <EmptyState
          variant="no-data"
          title="No Approved Applications"
          description="You have no applications that have been approved at the moment. Please check back later."
          icon={<FiAward className="h-12 w-12" />}
        />
      )}

      {data && data?.type === undefined && data.length > 0 && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-3 mb-6"
        >
          {data.map((v, i) => {
            const status = _status(v?.status);
            const feeStatus = _remita_paid(v?.acceptanceFee);

            return (
              <motion.div
                key={`_acceptance_${i}`}
                variants={rowVariants}
                className="bg-white rounded-xl border border-success-200 overflow-hidden"
              >
                {/* Main Row */}
                <div className="px-5 py-4">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-success-50 flex-shrink-0">
                        <FiCheckCircle className="h-5 w-5 text-success-600" />
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-neutral-900 font-mono">
                          {v?.code}
                        </span>
                        <p className="text-xs text-neutral-400 mt-0.5">
                          Applied {moment(v?.createdAt)} ago
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={status.variant}>{status.text}</Badge>
                      <Badge
                        variant={feeStatus.status === "paid" ? "success" : feeStatus.status === "unpaid" ? "warning" : "neutral"}
                        dot
                        size="sm"
                      >
                        {feeStatus.status === "paid" ? "Fee Paid" : feeStatus.status === "unpaid" ? "Fee Unpaid" : "Fee Pending"}
                      </Badge>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <FiFileText className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
                      <span className="text-neutral-600 truncate">
                        {v?.programme?.code} :: {v?.programme?.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FiUser className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
                      <span className="text-neutral-600">{v?.type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FiCalendar className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
                      <span className="text-neutral-600">
                        {v?.session?.code} :: {v?.session?.name}
                      </span>
                    </div>
                  </div>

                  {/* Fee Reference */}
                  {!is_null(v?.acceptanceFee) && (
                    <div className="mt-3 flex items-center gap-2 text-xs">
                      {feeStatus.status === "paid" ? (
                        <FiCheckCircle className="h-3.5 w-3.5 text-success-600 flex-shrink-0" />
                      ) : (
                        <FiAlertCircle className="h-3.5 w-3.5 text-warning-600 flex-shrink-0" />
                      )}
                      <span className={feeStatus.status === "paid" ? "text-success-700" : "text-warning-700"}>
                        {feeStatus.text}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions Footer */}
                <div className="px-5 py-3 bg-neutral-50 border-t border-neutral-100">
                  {is_null(v?.acceptanceFee) && (
                    <PayAcceptance v={v} />
                  )}

                  {!is_null(v?.acceptanceFee) && v?.acceptanceFee?.status === false && (
                    <div className="flex items-stretch gap-3">
                      <PayNowButton v={v} />
                      <AcceptanceWaiver v={v} />
                      <VerifyPayment v={v} />
                    </div>
                  )}

                  {!is_null(v?.acceptanceFee) && v?.acceptanceFee?.status === true && (
                    <PrintAcceptance v={v} />
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </>
  );
};

export default Acceptance;
